import { useMemo } from 'react';
import { Marker, Polyline, Popup, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import { useZoneStore } from '../../store/zoneStore';
import { useCouplingStore } from '../../store/couplingStore';
import { useReadOnlyUi } from '../../store/featureStore';
import { useSettingsStore } from '../../store/settingsStore';
import { useCouplingSwitch } from '../shared/CouplingSwitchDialog';
import { CouplingFlowFigures } from '../shared/CouplingFlowFigures';
import { loadingMapColor } from '../../utils/loadingConstants';
import { fmt } from '../../utils/numberFormat';
import { couplingPosition, otherZone, type ZoneCoupling } from '../../types/coupling';
import { parseUtcTimestamp } from '../../utils/timeUtils';

/**
 * Trennstellen of the active zone on the main map (doc/Architektur/NETZVERBUND.md 6).
 * Rendered as siblings of NetworkElements — never part of networkElementsStore, so the
 * coupling geometry stays out of map bounds, hit tests and statistics.
 *
 * Marker at the switch element (KVS/ONS in the neighbour zone, or our own KVS when we are
 * the neighbour): green link = closed (coupled, calculated as one network), orange
 * link_off = open. A dashed ring marks a deviation from the normal state. From the far
 * side (we are zone B) the neighbour's coupling cable is drawn as a dashed ghost line,
 * coloured by the loading its owner zone calculated and clickable with the Trennstelle
 * figures (couplingFlows) — no zone switch needed to read the cable's loading.
 */
export function TrennstellenLayer() {
  const activeZoneId = useZoneStore(s => s.activeZoneId);
  const couplings = useCouplingStore(s => s.couplings);
  const show = useCouplingStore(s => s.showTrennstellen);
  const readOnly = useReadOnlyUi();
  const { requestSwitch, dialog } = useCouplingSwitch();

  const items = useMemo(() => couplings.map(c => ({ c, pos: couplingPosition(c) })).filter(x => x.pos), [couplings]);

  if (!show || !activeZoneId) return null;

  return (
    <>
      {items.map(({ c, pos }) => (
        <CouplingMarker
          key={c.id}
          coupling={c}
          position={pos!}
          activeZoneId={activeZoneId}
          readOnly={readOnly}
          onSwitch={target => requestSwitch(c, target)}
        />
      ))}
      {dialog}
    </>
  );
}

const ICON_CLOSED = '#16a34a';
const ICON_OPEN = '#f97316';

// The marker sits AT the switch element (KVS/ONS), which has its own marker there. Drawn
// centred it covered the KVS icon and made it nearly unclickable, so the Trennstelle icon is
// pushed ~30 px away from the element towards the coupling cable (the side the cable enters).
const ICON_PX = 26;
const OFFSET_PX = 30;

function iconOffset(c: ZoneCoupling, position: [number, number]): [number, number] {
  const far = c.cableWaypoints.find(w => Math.abs(w[0] - position[0]) > 1e-7 || Math.abs(w[1] - position[1]) > 1e-7);
  // screen direction (north up): +lng = right, +lat = up
  let dx = far ? far[1] - position[1] : 1;
  let dy = far ? -(far[0] - position[0]) : -1;
  const len = Math.hypot(dx, dy) || 1;
  dx /= len; dy /= len;
  return [Math.round(dx * OFFSET_PX), Math.round(dy * OFFSET_PX)];
}

function couplingIcon(c: ZoneCoupling, selected: boolean, offset: [number, number]): L.DivIcon {
  const color = c.isClosed ? ICON_CLOSED : ICON_OPEN;
  const deviates = c.normalClosed != null && c.normalClosed !== c.isClosed;
  const ring = deviates ? 'border: 2px dashed #dc2626;' : 'border: 2px solid rgba(255,255,255,0.9);';
  const path = c.isClosed
    ? '<path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7"/><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7"/>'
    : '<path d="M18.8 11.3a5 5 0 0 0-.7-6.6 5 5 0 0 0-7-.1l-1.7 1.7"/><path d="M5.2 12.7a5 5 0 0 0 .7 6.6 5 5 0 0 0 7 .1l1.7-1.7"/><line x1="2" y1="2" x2="22" y2="22"/>';
  const html =
    `<div style="width:26px;height:26px;border-radius:9999px;background:${color};${ring}box-shadow:0 1px 4px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;${selected ? 'outline:3px solid #facc15;' : ''}">` +
    `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">${path}</svg></div>`;
  // iconAnchor is the point of the icon placed at the marker position: shifting it by -offset
  // moves the drawn icon by +offset away from the element
  return L.divIcon({
    html,
    className: 'trennstelle-marker',
    iconSize: [ICON_PX, ICON_PX],
    iconAnchor: [ICON_PX / 2 - offset[0], ICON_PX / 2 - offset[1]],
    popupAnchor: [offset[0], offset[1] - ICON_PX / 2],
  });
}

function stateLabel(c: ZoneCoupling): string {
  return c.isClosed ? 'geschlossen' : 'offen';
}

function sourceLabel(source: string): string {
  switch (source) {
    case 'import': return 'Import';
    case 'manual': return 'manuell';
    case 'measurement': return 'Messung';
    default: return source;
  }
}

function CouplingMarker({ coupling: c, position, activeZoneId, readOnly, onSwitch }: {
  coupling: ZoneCoupling;
  position: [number, number];
  activeZoneId: string;
  readOnly: boolean;
  onSwitch: (target: boolean) => void;
}) {
  const selectedId = useCouplingStore(s => s.selectedCouplingId);
  const selectCoupling = useCouplingStore(s => s.selectCoupling);
  const flow = useCouplingStore(s => s.flows[c.id]);
  const neighbour = otherZone(c, activeZoneId);
  const weAreOwner = c.zoneAId === activeZoneId;
  // Zone B: the switch element (KVS/ONS) has its own marker here → push the icon aside.
  // Zone A: the Kuppelpunkt is not drawn as a sleeve, the icon IS the cable end → centred.
  const offset = useMemo<[number, number]>(() => (weAreOwner ? [0, 0] : iconOffset(c, position)), [c, position, weAreOwner]);
  const icon = useMemo(() => couplingIcon(c, selectedId === c.id, offset), [c, selectedId, offset]);
  const changedAt = parseUtcTimestamp(c.stateChangedAt);
  const deviates = c.normalClosed != null && c.normalClosed !== c.isClosed;

  return (
    <>
      {!weAreOwner && c.cableWaypoints.length > 1 && (
        <GhostCable coupling={c} neighbourName={neighbour.name} activeZoneId={activeZoneId} />
      )}
      <Marker
        position={position}
        icon={icon}
        zIndexOffset={900}
        eventHandlers={{ click: () => selectCoupling(c.id) }}
      >
        <Tooltip direction="top" offset={[offset[0], offset[1] - ICON_PX / 2 - 1]}>
          Trennstelle → {neighbour.name} · {c.switchElementName ?? c.switchElementId} · {stateLabel(c)}
        </Tooltip>
        <Popup minWidth={240} maxWidth={320}>
          <div className="text-sm space-y-1.5">
            <div className="font-semibold text-gray-900">{c.name}</div>
            <div className="text-gray-700">
              Nachbarzone: <span className="font-medium">{neighbour.name}</span>
              <br />Schaltstelle: {c.switchElementType === 'trafo' ? 'ONS' : 'KVS'} {c.switchElementName ?? c.switchElementId}
              {c.cableName ? <><br />Kuppelkabel: {c.cableName}</> : null}
            </div>
            <div className="flex items-center gap-2">
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold text-white"
                style={{ backgroundColor: c.isClosed ? ICON_CLOSED : ICON_OPEN }}
              >
                {c.isClosed ? 'Geschlossen' : 'Offen'}
              </span>
              {c.normalClosed != null && (
                <span className={`text-xs ${deviates ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                  Normal: {c.normalClosed ? 'geschlossen' : 'offen'}{deviates ? ' — Abweichung' : ''}
                </span>
              )}
            </div>
            {/* Figures of the coupling cable — the line itself may be hidden under the icons */}
            <CouplingFlowFigures coupling={c} flow={flow} activeZoneId={activeZoneId} variant="popup" />
            <div className="text-xs text-gray-500">
              {c.isClosed ? 'Beide Zonen werden als ein Netz gerechnet.' : 'Das Kuppelkabel endet hier blind.'}
              <br />Seit {changedAt.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })} ({sourceLabel(c.stateSource)}{c.stateChangedBy ? `, ${c.stateChangedBy}` : ''})
            </div>
            {!readOnly && (
              <button
                type="button"
                onClick={() => onSwitch(!c.isClosed)}
                className="mt-1 w-full px-3 py-1.5 rounded-md text-xs font-semibold text-white"
                style={{ backgroundColor: c.isClosed ? ICON_OPEN : ICON_CLOSED }}
              >
                {c.isClosed ? 'Trennstelle öffnen' : 'Trennstelle schließen'}
              </button>
            )}
          </div>
        </Popup>
      </Marker>
    </>
  );
}

const GHOST_NEUTRAL = '#6b7280';

/**
 * Zone B view of the neighbour's coupling cable: a dashed ghost line coloured by the loading the
 * owner zone calculated for it, with a popup showing the Trennstelle figures (loading, current,
 * power and its direction). Clicking also selects the coupling so its marker lights up.
 */
function GhostCable({ coupling: c, neighbourName, activeZoneId }: {
  coupling: ZoneCoupling;
  neighbourName: string;
  activeZoneId: string;
}) {
  const flow = useCouplingStore(s => s.flows[c.id]);
  const selectCoupling = useCouplingStore(s => s.selectCoupling);
  const thresholds = useSettingsStore(s => s.thresholds.cableLoadLevels);
  const loading = flow?.loadingPercent ?? null;
  const color = loading != null ? loadingMapColor(loading, thresholds) : c.isClosed ? ICON_CLOSED : GHOST_NEUTRAL;

  return (
    <Polyline
      positions={c.cableWaypoints}
      pathOptions={{ color, dashArray: '6 6', weight: loading != null ? 4 : 3, opacity: 0.85 }}
      eventHandlers={{ click: () => selectCoupling(c.id) }}
    >
      <Tooltip sticky>
        Kuppelkabel {c.cableName ?? ''} aus Zone {neighbourName} · Trennstelle {stateLabel(c)}
        {loading != null ? ` · ${fmt(loading, 1)} %` : ''}
      </Tooltip>
      <Popup minWidth={250} maxWidth={320}>
        <div className="text-sm space-y-1.5">
          <div className="font-semibold text-gray-900">Kuppelkabel {c.cableName ?? c.cableId}</div>
          <div className="text-xs text-gray-700">
            Gehört zu Zone <span className="font-medium">{neighbourName}</span> · Trennstelle{' '}
            <span className="font-semibold" style={{ color: c.isClosed ? ICON_CLOSED : ICON_OPEN }}>{stateLabel(c)}</span>
          </div>
          <CouplingFlowFigures coupling={c} flow={flow} activeZoneId={activeZoneId} variant="popup" />
        </div>
      </Popup>
    </Polyline>
  );
}
